import type { AuthoringBlockNode, AuthoringDoc, Variant } from '@personalizehq/schemas';
import { DEFAULT_VARIANT_KEY } from '@personalizehq/schemas';
import { newId, stepsOf } from './doc.js';
import { cloneSubtreesWithFreshIds } from './tree.js';

// VARIANT OPS (ADR 0015). Like the step ops, these MUTATE inside an immer draft (`applyChange`), so
// each is one undo checkpoint. A variant is a full alternative step-list sharing the doc's theme
// and labels map — the ops below never touch `theme`, and labels ride node ids.

/** `authoringDocSchema`'s bound (`variants: .max(7)`), mirrored the way MAX_STEPS mirrors steps'. */
export const MAX_VARIANTS = 7;

/** The KEY is minted once, here, and never changes: it is the analytics identity every exposure
 *  event carries for the life of the experiment (ADR 0016). Renames touch only the display name. */
export function slugifyVariantKey(name: string, taken: ReadonlySet<string>): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'variant';
  if (!taken.has(base) && base !== DEFAULT_VARIANT_KEY) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base}_${i}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** Deep-copy of the SOURCE list (`fromKey` null = the default steps): fresh step ids, fresh node
 *  ids via the clipboard's clone machinery, and the source nodes' labels carried across through the
 *  id map — a duplicated tree keeps its names, exactly as paste does. */
export function createVariant(
  doc: AuthoringDoc,
  options: { name: string; fromKey?: string | null }
): Variant {
  const variants = (doc.variants ??= []);
  if (variants.length >= MAX_VARIANTS) {
    throw new Error(`createVariant: an experience may hold at most ${MAX_VARIANTS} variants`);
  }
  const name = options.name.trim().slice(0, 80) || 'Variant';
  const taken = new Set(variants.map((v) => v.key));
  const key = slugifyVariantKey(name, taken);

  const source = stepsOf(doc, options.fromKey ?? null);
  const steps: Variant['steps'] = source.map((step) => {
    const { clones, idMap } = cloneSubtreesWithFreshIds([step.root]);
    for (const [oldId, freshId] of idMap) {
      const label = doc.labels?.[oldId];
      if (label) (doc.labels ??= {})[freshId] = label;
    }
    return {
      ...step,
      id: newId(),
      root: clones[0]!,
    };
  });

  const variant: Variant = { key, name, steps };
  variants.push(variant);
  return variant;
}

/** Display name only — see the key-immutability note above. */
export function renameVariant(doc: AuthoringDoc, key: string, name: string): void {
  const variant = (doc.variants ?? []).find((v) => v.key === key);
  if (!variant) throw new Error(`renameVariant: unknown variant "${key}"`);
  variant.name = name.trim().slice(0, 80) || variant.name;
}

/** Deleting is allowed even while a campaign's allocation names this key: the editor cannot see
 *  campaigns, and publish already refuses a dangling arm LOUDLY (bundle-compiler). The confirm
 *  dialog carries that warning. Labels for the variant's nodes are pruned so the doc does not
 *  accumulate entries nothing can reach. */
export function deleteVariant(doc: AuthoringDoc, key: string): void {
  const variants = doc.variants ?? [];
  const index = variants.findIndex((v) => v.key === key);
  if (index === -1) throw new Error(`deleteVariant: unknown variant "${key}"`);

  if (doc.labels) {
    const removed = variants[index]!;
    const prune = (nodeId: string) => delete doc.labels![nodeId];
    for (const step of removed.steps) walkIds(step.root, prune);
  }
  variants.splice(index, 1);
  if (variants.length === 0) delete doc.variants;
}

function walkIds(node: AuthoringBlockNode, visit: (id: string) => void): void {
  visit(node.id);
  if ('children' in node) for (const child of node.children) walkIds(child, visit);
  if (node.block === 'repeat') walkIds(node.template, visit);
  if (node.fallback) walkIds(node.fallback, visit);
}
